import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, navigate } from '../../app/router';
import type { Board, Org } from '../../lib/api';
import { Button, Dialog, Field, Input, Select } from '../../ui';
import { DialogClose } from '../../ui/DialogClose';
import { useInbox, useInvalidateContent, useProjects, withInbox } from './queries';

/** "Duplicate to…" (spec §12): a copy of the board in a project of the organization, made by the Worker; opens it. */
export function DuplicateBoardDialog({ board, org, onClose }: { board: Board; org: Org; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const projects = useProjects(org.id);
  const inbox = useInbox(org.id);
  const options = withInbox(
    inbox.data,
    t('dashboard.noProject'),
    [...(projects.data ?? [])].filter((p) => !p.deletedAt && !p.archivedAt).sort((a, b) => a.name.localeCompare(b.name)),
  );
  const [projectId, setProjectId] = useState(board.projectId);
  const [title, setTitle] = useState(() => t('dashboard.copyOf', { title: board.title }).slice(0, 200));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !title.trim()) return;
    setBusy(true);
    setError(false);
    try {
      const copy = await api.duplicateBoard(board.id, { projectId, title: title.trim() });
      await invalidate();
      onClose();
      navigate(href('board', { boardId: copy.boardId }));
    } catch {
      setError(true);
      setBusy(false);
    }
  };

  return (
    <Dialog title={t('dashboard.duplicateBoard')} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <Field label={t('dashboard.project')}>
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('dashboard.boardCopyTitle')} error={error ? t('auth.genericError') : null}>
          <Input value={title} maxLength={200} enterKeyHint="done" onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2">
          <DialogClose>
            {t('dashboard.cancel')}
          </DialogClose>
          <Button variant="primary" type="submit" disabled={busy || !title.trim()}>
            {t('dashboard.duplicate')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
